import { Link, createFileRoute } from "@tanstack/react-router";

import { CihClosingCta } from "~/components/cih/closing-cta";
import { FRAMEWORK_TYPE_META } from "~/components/cih/constants";
import { CihFrameworkCards } from "~/components/cih/framework-cards";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import { NamedIcon } from "~/components/ui/named-icon";
import { getPublishedFrameworksFn } from "~/lib/functions/cih/frameworks";
import { FrameworkType } from "~/lib/schemas/db-enums-cih";
import { BRAND_URL } from "~/lib/shared/brand";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";

const CANONICAL_URL = `${BRAND_URL}/frameworks`;

export const Route = createFileRoute("/_public/frameworks/")({
  staticData: { title: "Frameworks" },
  head: () => ({
    ...createBrandMeta({
      title: "Coaching Frameworks | Dream Coach Match",
      description:
        "Browse evidence-based coaching frameworks, methodologies, standards, and protocols used by expert coaches worldwide.",
      ogUrl: CANONICAL_URL,
      canonical: CANONICAL_URL,
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          createBreadcrumbStructuredData([
            { name: "Home", url: BRAND_URL },
            { name: "Frameworks", url: CANONICAL_URL },
          ]),
        ),
      },
    ],
  }),
  loader: async () => {
    const frameworks = await getPublishedFrameworksFn();
    return { frameworks };
  },
  component: FrameworksPage,
});

const TYPE_ORDER: FrameworkType[] = [
  FrameworkType.Framework,
  FrameworkType.Methodology,
  FrameworkType.Standard,
  FrameworkType.Protocol,
];

function FrameworksPage() {
  const { frameworks } = Route.useLoaderData();

  const frameworkTypes = Object.values(FrameworkType) as string[];
  const grouped = frameworks.reduce((map, fw) => {
    if (!frameworkTypes.includes(fw.type)) return map;
    const type = fw.type as FrameworkType;
    const items = map.get(type) ?? [];
    map.set(type, [...items, fw]);
    return map;
  }, new Map<FrameworkType, typeof frameworks>());

  const groupsWithItems = TYPE_ORDER.flatMap((type) => {
    const items = grouped.get(type);
    return items && items.length > 0 ? [{ type, items }] : [];
  });

  return (
    <>
      <section className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-6 pb-14 md:pt-8 md:pb-20 xl:px-0">
          <Breadcrumb
            items={[{ label: "Home", href: "/" }, { label: "Frameworks" }]}
            isBold={false}
          />
          <div className="mt-6 text-xs font-medium tracking-[0.22em] text-green-700 uppercase">
            ● Coaching Intelligence
          </div>
          <h1 className="mt-4 font-heading text-4xl font-medium tracking-tight text-gray-900 md:text-5xl lg:text-6xl xl:text-7xl">
            Coaching frameworks
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-normal text-gray-700">
            Evidence-based methodologies, standards, and protocols used by
            expert coaches worldwide.
          </p>
        </div>
      </section>

      {groupsWithItems.length === 0 ? (
        <EmptyState />
      ) : (
        groupsWithItems.map(({ type, items }) => {
          const meta = FRAMEWORK_TYPE_META[type];
          return (
            <CihFrameworkCards
              key={type}
              frameworks={items}
              sectionLabel={meta.pluralLabel}
              heading={meta.pluralLabel}
            />
          );
        })
      )}

      <CihClosingCta
        headline="Find a coach who uses the right framework for your situation."
        cta={
          <Link to="/discovery">
            <Button variant="primary" size="lg">
              Start →
            </Button>
          </Link>
        }
      />
    </>
  );
}

function EmptyState() {
  return (
    <section className="mx-auto max-w-md px-4 py-20 text-center">
      <NamedIcon name="target" className="mx-auto h-10 w-10 text-gray-300" />
      <p className="mt-4 text-sm text-gray-500">
        No frameworks published yet. Check back soon.
      </p>
    </section>
  );
}
